import { useEffect, useState, useCallback } from "react";
import { ShoppingBag, Clock, ChefHat, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUS = {
  new: { label: "New", cls: "bg-primary/15 text-primary", next: "preparing", nextLabel: "Start preparing" },
  preparing: { label: "Preparing", cls: "bg-accent text-accent-foreground", next: "ready", nextLabel: "Mark ready" },
  ready: { label: "Ready", cls: "bg-secondary/15 text-secondary", next: "completed", nextLabel: "Complete" },
  completed: { label: "Completed", cls: "bg-muted text-muted-foreground", next: null },
  cancelled: { label: "Cancelled", cls: "bg-destructive/15 text-destructive", next: null },
};

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [tab, setTab] = useState("active");

  const load = useCallback(() => api.get("/orders").then((r) => setOrders(r.data)), []);
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      toast.success(`Order ${status}`);
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  if (!orders) return <Loading />;
  const active = orders.filter((o) => ["new", "preparing", "ready"].includes(o.status));
  const shown = tab === "active" ? active : orders;

  return (
    <div data-testid="orders-page">
      <PageHeader
        title="Online Orders"
        subtitle="Incoming orders from your QR menu — update status as you go."
        action={<Button variant="outline" onClick={load} className="border-border hover:bg-muted" data-testid="refresh-orders"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>}
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="active" data-testid="orders-tab-active">Active ({active.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="orders-tab-all">All orders</TabsTrigger>
        </TabsList>
      </Tabs>

      {shown.length === 0 ? (
        <EmptyState icon={ShoppingBag} title="No orders yet" desc="Orders placed from your public QR menu will show up here in real time." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((o) => {
            const st = STATUS[o.status] || STATUS.new;
            return (
              <div key={o.id} data-testid={`order-${o.id}`} className="bg-card border border-border rounded-xl p-5 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">#{o.number} · {o.customer_name}</p>
                    <p className="text-sm text-muted-foreground capitalize flex items-center gap-1.5 mt-0.5">
                      <Clock className="h-3.5 w-3.5" /> {o.order_type}{o.table ? ` · Table ${o.table}` : ""}
                    </p>
                  </div>
                  <Badge className={`${st.cls} border-0 hover:${st.cls}`}>{st.label}</Badge>
                </div>
                <div className="mt-4 space-y-1.5 text-sm flex-1">
                  {o.items.map((it, i) => (
                    <div key={`${it.id}-${i}`} className="flex justify-between">
                      <span className="text-foreground/80">{it.qty}× {it.name}</span>
                      <span className="text-muted-foreground">${(it.price * it.qty).toFixed(2)}</span>
                    </div>
                  ))}
                  {o.notes && <p className="italic text-muted-foreground pt-1">"{o.notes}"</p>}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <span className="font-heading text-lg font-semibold">${Number(o.total).toFixed(2)}{o.tip ? <span className="text-xs text-muted-foreground font-normal"> (incl. ${Number(o.tip).toFixed(2)} tip)</span> : null}</span>
                  {o.payment_status === "paid"
                    ? <Badge className="bg-secondary/15 text-secondary border-0 hover:bg-secondary/15">Paid</Badge>
                    : o.payment_status === "unpaid"
                      ? <Badge variant="outline" className="text-muted-foreground">Pay in person</Badge>
                      : null}
                </div>
                {st.next && (
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" data-testid={`advance-order-${o.id}`} onClick={() => setStatus(o.id, st.next)}
                      className="flex-1 bg-secondary hover:bg-[#4B5845] text-secondary-foreground">
                      <CheckCircle2 className="h-4 w-4 mr-1" /> {st.nextLabel}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setStatus(o.id, "cancelled")} data-testid={`cancel-order-${o.id}`}>
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
